import {faLink} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {zodResolver} from '@hookform/resolvers/zod';
import {Card, CardContent, CardHeader, Divider, Stack} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {ActionParams, HttpError, useSelect} from '@refinedev/core';
import {useForm} from '@refinedev/react-hook-form';
import {FC, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router';
import {z as zod} from 'zod';

import {Create} from '@components/crud/create';
import {Edit} from '@components/crud/edit';
import {Field, Form} from '@minimal/components/hook-form';
import {AccountSchema} from '@pages/pilotage-rh/accounts/account-types';
import {ConnectorForm} from '@pages/pilotage-rh/accounts/components/connector-form';
import {SubscriptionsForm} from '@pages/pilotage-rh/accounts/components/subscriptions-form';

export type CreateAccountSchemaType = zod.infer<typeof AccountSchema>;

export const NewEditForm: FC<ActionParams> = ({action}) => {
  const {t} = useTranslation();

  const defaultValues: CreateAccountSchemaType = {
    email: '',
    label: '',
    subscriptions: [],
    firstName: '',
    lastName: '',
    bdeseId: null,
    users: [],
    connectorDSN: null,
  };

  const methods = useForm<CreateAccountSchemaType, HttpError, CreateAccountSchemaType>({
    refineCoreProps: {
      action,
      successNotification: (data, values) => {
        return {
          message: t(`pages.pilotage-rh.account.notifications.${action === 'edit' ? 'update' : 'creation'}.success`),
          type: 'success',
        };
      },
      errorNotification: (data, values) => {
        return {
          message: t(`pages.pilotage-rh.account.notifications.${action === 'edit' ? 'update' : 'creation'}.error`),
          type: 'error',
        };
      },
    },
    mode: 'all',
    resolver: zodResolver(AccountSchema),
    defaultValues,
  });

  const {error} = methods.refineCore.mutation;
  useEffect(() => {
    if (error && error.statusCode === 422 && error.response.data) {
      Object.entries(error.response.data.errors).forEach(([field, message]) => {
        methods.setError(field as keyof CreateAccountSchemaType, {
          type: 'server',
          message: message as string,
        });
      });
    }
  }, [error]);

  const {
    saveButtonProps,
    watch,
    refineCore: {
      // @ts-ignore
      query: {isLoading: isLoadingAccount, data},
    },
    formState: {errors},
  } = methods;

  const users = watch('users') ?? [];
  const bdeseId = watch('bdeseId') ?? '';
  const connectorDSN = watch('connectorDSN') ?? '';

  const {
    query: {isLoading: isLoadingOptions},
  } = useSelect({
    resource: 'workspaces',
    defaultValue: data?.data?.workspaces,
    optionLabel: 'label',
    optionValue: 'value',
  });

  const [isErrorSubscriptions, setIsErrorSubscriptions] = useState(false);
  useEffect(() => {
    setIsErrorSubscriptions(!!methods.formState?.errors?.subscriptions);
  }, [methods.formState?.errors?.subscriptions]);

  const [isErrorConnector, setIsErrorConnector] = useState(false);

  useEffect(() => {
    setIsErrorConnector(!!methods.formState?.errors?.connectorDSN);
    console.log(methods.formState?.errors?.connectorDSN);
  }, [methods.formState?.errors?.connectorDSN]);

  const WrapperComponent = action === 'create' ? Create : Edit;

  const wrapperProps = action === 'edit' ? {canDelete: false} : {};
  const bdeseUrl = import.meta.env.VITE_BDESE_URL || '';

  return (
    <div style={{maxWidth: '600px'}}>
      <Form methods={methods}>
        <WrapperComponent
          saveButtonProps={saveButtonProps}
          isLoading={(action === 'edit' && isLoadingAccount) || isLoadingOptions}
          headerButtons={
            <Button component={Link} to={`/pilotage-rh/accounts`} variant="outlined">
              {t('labels.back_to_multiples', {name: t('labels.accounts')})}
            </Button>
          }
          {...wrapperProps}
        >
          <Stack spacing={4}>
            <Card>
              <CardContent>
                <Stack gap={2}>
                  <Field.Text
                    id="label"
                    name="label"
                    margin="none"
                    fullWidth
                    label={t('pages.pilotage-rh.account.fields.label')}
                  />
                  {bdeseId && (
                    <Stack>
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, pb: 3}}>
                        <FontAwesomeIcon icon={faLink} size={'xs'} color={'info'} />
                        <Typography color={'info'} variant={'subtitle2'}>
                          <Link to={`${bdeseUrl}/admin/company/edit/${bdeseId}`} about="_blank">
                            {t('pages.pilotage-rh.account.fields.bdeseId')} {bdeseId}
                          </Link>
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
            <Card>
              <Stack divider={<Divider />} spacing={2}>
                <CardHeader title={t('pages.pilotage-rh.account.fields.owner')} />
                <CardContent>
                  <Stack gap={2}>
                    <Field.Text
                      id="email"
                      name="email"
                      margin="none"
                      fullWidth
                      label={action === 'edit' ? '' : t('pages.pilotage-rh.account.fields.email')}
                      disabled={action === 'edit'}
                    />
                    <Field.Text
                      id="firstName"
                      name="firstName"
                      margin="none"
                      fullWidth
                      label={t('pages.pilotage-rh.account.fields.firstName')}
                      disabled={action === 'edit'}
                    />
                    <Field.Text
                      id="lastName"
                      name="lastName"
                      margin="none"
                      fullWidth
                      label={t('pages.pilotage-rh.account.fields.lastName')}
                      disabled={action === 'edit'}
                    />
                  </Stack>
                </CardContent>
              </Stack>
            </Card>
            <SubscriptionsForm error={isErrorSubscriptions} />

            {action === 'edit' && (
              <Card>
                <Stack divider={<Divider />} spacing={2}>
                  <CardHeader title={t('pages.pilotage-rh.account.fields.users')} />
                  <CardContent>
                    <ul style={{fontSize: '14px'}}>
                      {users.map((user) => (
                        <li
                          key={user.uuid}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                          }}
                        >
                          <span>{`${user.firstName} ${user.lastName} `}</span>
                          <Link to={`mailto:${user.email}`}>{user.email}</Link>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Stack>
              </Card>
            )}

            {action === 'edit' && <ConnectorForm error={isErrorConnector} />}
          </Stack>
        </WrapperComponent>
      </Form>
      <div style={{display: 'flex', justifyContent: 'flex-end'}}>
        <Button
          component={Link}
          to={`/pilotage-rh/accounts`}
          variant="outlined"
          sx={{mr: '1em', position: 'relative', top: '-52px', left: '-140px'}}
        >
          {t('buttons.cancel')}
        </Button>
      </div>
    </div>
  );
};
